import React from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { trackAuthEvent } from '../../../services/analytics';
import type { useChangePlan } from '../hooks/useChangePlan';
import { normalizeBillingCycleForTier } from '../planBilling';
import { planDisplayName } from '../profileFormat';
import { COLORS } from '../profileStyles';
import type { Translate } from '../profileTypes';
import { checkboxA11y } from '@/utils/checkboxA11y';

interface ChangePlanModalProps {
  plan: ReturnType<typeof useChangePlan>;
  /** The subscription's current plan tier, for the warning copy. */
  currentPlanTier?: string;
  windowWidth: number;
  t: Translate;
}

export function ChangePlanModal({ plan, currentPlanTier, windowWidth, t }: ChangePlanModalProps) {
  const {
    showChangePlan,
    closeChangePlan,
    planLoading,
    handlePlanDetailsLink,
    planOptions,
    planForm,
    setPlanForm,
    billingCycleOptions,
    planPricingStatus,
    getCurrentPlanPriceLabel,
    getPlanPriceLabel,
    planAcknowledged,
    setPlanAcknowledged,
    handleChangePlanSubmit,
    planPreviewStatus,
    planChangeCopy,
  } = plan;
  // BIL-L05: confirmable only once Stripe's preview says what happens, and only for a real change.
  const canConfirm = Boolean(planChangeCopy) && planChangeCopy?.kind !== 'same_plan';
  const getPlanDisplayName = (planTier?: string) => planDisplayName(t, planTier);
  const isDesktopDialog = windowWidth >= 860;

  return (
    <Modal
      visible={showChangePlan}
      animationType="fade"
      transparent
      onRequestClose={() => closeChangePlan()}
    >
    <KeyboardAvoidingView
      behavior="height"
      style={changePlanStyles.keyboardView}
    >
      <View style={changePlanStyles.backdrop}>
        <View
          style={[
            changePlanStyles.dialog,
            isDesktopDialog && changePlanStyles.dialogDesktop,
            changePlanStyles.dialogWeb,
          ]}
          accessibilityViewIsModal
        >
          <View style={changePlanStyles.header}>
            <View style={changePlanStyles.headerCopy}>
              <Text style={changePlanStyles.title}>{t('profile.changePlanTitle')}</Text>
              <Text style={changePlanStyles.description}>
                {t('profile.changePlanDescription')}
              </Text>
            </View>
            <TouchableOpacity
              style={changePlanStyles.closeButton}
              onPress={() => closeChangePlan()}
              accessibilityRole="button"
              accessibilityLabel={t('profile.cancel')}
              disabled={planLoading}
            >
              <MaterialIcons name="close" size={20} color={COLORS.text.secondary} />
            </TouchableOpacity>
          </View>

          <ScrollView
            style={changePlanStyles.scrollArea}
            contentContainerStyle={changePlanStyles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <View style={[changePlanStyles.contentGrid, isDesktopDialog && changePlanStyles.contentGridDesktop]}>
              <View style={changePlanStyles.selectionColumn}>
                <View style={changePlanStyles.sectionHeadingRow}>
                  <Text style={changePlanStyles.sectionHeading}>{t('profile.planTierLabel')}</Text>
                  <TouchableOpacity
                    style={changePlanStyles.comparisonLink}
                    onPress={handlePlanDetailsLink}
                    accessibilityRole="link"
                  >
                    <Text style={changePlanStyles.comparisonLinkText}>
                      {t('profile.viewPlanDetails')}
                    </Text>
                    <MaterialIcons name="open-in-new" size={16} color={COLORS.primary} />
                  </TouchableOpacity>
                </View>

                <View style={changePlanStyles.planList}>
                  {planOptions.map((option) => {
                    const selected = planForm.tier === option.id;
                    return (
                      <TouchableOpacity
                        key={option.id}
                        style={[
                          changePlanStyles.planOption,
                          selected && changePlanStyles.planOptionSelected,
                        ]}
                        onPress={() => {
                          trackAuthEvent('plan_option_selected', { plan_tier: option.id });
                          setPlanForm((prev) => ({
                            tier: option.id,
                            billingCycle: normalizeBillingCycleForTier(option.id, prev.billingCycle),
                          }));
                        }}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selected, disabled: planLoading }}
                        disabled={planLoading}
                      >
                        <View style={changePlanStyles.planOptionCopy}>
                          <Text style={changePlanStyles.planOptionTitle}>{option.title}</Text>
                          <Text style={changePlanStyles.planOptionDescription}>{option.description}</Text>
                        </View>
                        <View style={[changePlanStyles.radio, selected && changePlanStyles.radioSelected]}>
                          {selected && <View style={changePlanStyles.radioDot} />}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <Text style={changePlanStyles.billingHeading}>{t('profile.billingCycleLabel')}</Text>
                <View style={changePlanStyles.billingCycleRow}>
                  {billingCycleOptions.map((option) => {
                    const selected = planForm.billingCycle === option.id;
                    return (
                      <TouchableOpacity
                        key={option.id}
                        style={[
                          changePlanStyles.billingCycleOption,
                          selected && changePlanStyles.billingCycleOptionSelected,
                        ]}
                        onPress={() => {
                          trackAuthEvent('billing_cycle_selected', { billing_cycle: option.id });
                          setPlanForm((prev) => ({
                            ...prev,
                            billingCycle: normalizeBillingCycleForTier(prev.tier, option.id),
                          }));
                        }}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selected, disabled: planLoading }}
                        disabled={planLoading}
                      >
                        <Text
                          style={[
                            changePlanStyles.billingCycleText,
                            selected && changePlanStyles.billingCycleTextSelected,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              <View style={changePlanStyles.summaryColumn}>
                <View style={changePlanStyles.warningCard}>
                  <View style={changePlanStyles.warningHeadingRow}>
                    <View style={changePlanStyles.warningIcon}>
                      <MaterialIcons name="bolt" size={18} color="#B45309" />
                    </View>
                    <Text style={changePlanStyles.warningTitle}>
                      {planChangeCopy?.title ?? t('profile.planChangeSummaryTitle')}
                    </Text>
                  </View>
                  <Text style={changePlanStyles.warningMessage} testID="plan-change-preview-message">
                    {planChangeCopy?.message
                      ?? t('profile.planChangePreviewLoading', {
                        current: getPlanDisplayName(currentPlanTier),
                        target: getPlanDisplayName(planForm.tier),
                      })}
                  </Text>
                  {planChangeCopy?.pauseNotice ? (
                    <Text style={changePlanStyles.warningMessage} testID="plan-change-pause-notice">
                      {planChangeCopy.pauseNotice}
                    </Text>
                  ) : null}
                  {(planPricingStatus === 'loading' || planPreviewStatus === 'loading') && (
                    <View style={changePlanStyles.loadingPricingRow}>
                      <ActivityIndicator size="small" color={COLORS.secondary} />
                      <Text style={changePlanStyles.loadingPricingText}>
                        {t('profile.loadingPricing')}
                      </Text>
                    </View>
                  )}
                  <View style={changePlanStyles.priceSummary}>
                    <View style={changePlanStyles.priceRow}>
                      <Text style={changePlanStyles.priceLabel}>{t('profile.currentPlanLabel')}</Text>
                      <Text style={changePlanStyles.priceValue}>{getCurrentPlanPriceLabel()}</Text>
                    </View>
                    <View style={changePlanStyles.priceDivider} />
                    <View style={changePlanStyles.priceRow}>
                      <Text style={changePlanStyles.priceLabel}>{t('profile.newPlanLabel')}</Text>
                      <Text style={changePlanStyles.priceValueStrong}>
                        {getPlanPriceLabel(planForm.tier, planForm.billingCycle)}
                      </Text>
                    </View>
                    {planChangeCopy ? (
                      <>
                        <View style={changePlanStyles.priceDivider} />
                        <View style={changePlanStyles.priceRow}>
                          <Text style={changePlanStyles.priceLabel}>{t('profile.planChangeDueTodayLabel')}</Text>
                          <Text style={changePlanStyles.priceValueStrong} testID="plan-change-due-today">
                            {planChangeCopy.dueToday}
                          </Text>
                        </View>
                      </>
                    ) : null}
                  </View>
                  <Text style={changePlanStyles.billingNote}>
                    {planChangeCopy?.renewal ?? t('profile.planBillingNote', {
                      price: getPlanPriceLabel(planForm.tier, planForm.billingCycle),
                      interval: planForm.billingCycle === '6mo'
                        ? (t('profile.billingCycle6mo') || '6 months').toLowerCase()
                        : planForm.billingCycle === '3mo'
                        ? (t('profile.billingCycle3mo') || '3 months').toLowerCase()
                        : (t('profile.billingCycle1mo') || '1 month').toLowerCase(),
                    })}
                  </Text>
                </View>

                <TouchableOpacity
                  style={[
                    changePlanStyles.acknowledgement,
                    planAcknowledged && changePlanStyles.acknowledgementSelected,
                  ]}
                  onPress={() => {
                    setPlanAcknowledged((prev) => {
                      const next = !prev;
                      trackAuthEvent('plan_acknowledged_toggled', { enabled: next });
                      return next;
                    });
                  }}
                  {...checkboxA11y(planAcknowledged, planLoading || !canConfirm)}
                  disabled={planLoading || !canConfirm}
                >
                  <MaterialIcons
                    name={planAcknowledged ? 'check-box' : 'check-box-outline-blank'}
                    size={22}
                    color={planAcknowledged ? COLORS.primary : '#6B7280'}
                  />
                  <Text style={changePlanStyles.acknowledgementText}>
                    {planChangeCopy?.acknowledgement ?? t('profile.planAcknowledgeDowngradeLabel')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>

          <View style={[changePlanStyles.footer, isDesktopDialog && changePlanStyles.footerDesktop]}>
            <Button
              variant="secondary"
              size="lg"
              onPress={() => closeChangePlan()}
              disabled={planLoading}
              style={[changePlanStyles.footerButton, !isDesktopDialog && changePlanStyles.footerButtonMobile]}
            >
              <Text className="font-medium">{t('profile.cancel')}</Text>
            </Button>
            <Button
              variant="default"
              size="lg"
              onPress={handleChangePlanSubmit}
              disabled={planLoading || !planAcknowledged || !canConfirm}
              style={[changePlanStyles.footerButton, !isDesktopDialog && changePlanStyles.footerButtonMobile]}
            >
              {planLoading ? (
                <ActivityIndicator color="white" />
              ) : (
                <Text className="text-primary-foreground font-semibold">{t('profile.updatePlan')}</Text>
              )}
            </Button>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
    </Modal>
  );
}

const changePlanStyles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  backdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backgroundColor: 'rgba(15, 23, 42, 0.52)',
  },
  dialog: {
    width: '100%',
    maxWidth: 520,
    maxHeight: '92%',
    flexShrink: 1,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#D9E0EA',
    backgroundColor: '#FFFFFF',
    shadowColor: '#17305C',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.24,
    shadowRadius: 32,
    elevation: 18,
  },
  dialogDesktop: {
    maxWidth: 900,
    maxHeight: '88%',
  },
  dialogWeb: {
    boxShadow: '0 32px 80px rgba(15, 31, 68, 0.24), 0 8px 24px rgba(30, 64, 175, 0.12)',
  } as any,
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    paddingHorizontal: 28,
    paddingTop: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E7EBF1',
  },
  headerCopy: {
    flex: 1,
  },
  title: {
    color: COLORS.text.primary,
    fontSize: 23,
    lineHeight: 29,
    fontWeight: '800',
    letterSpacing: -0.25,
  },
  description: {
    maxWidth: 520,
    marginTop: 5,
    color: COLORS.text.secondary,
    fontSize: 14,
    lineHeight: 20,
  },
  closeButton: {
    width: 38,
    height: 38,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DDE3EC',
    backgroundColor: '#F8FAFC',
  },
  scrollArea: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 28,
  },
  contentGrid: {
    gap: 24,
  },
  contentGridDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 30,
  },
  selectionColumn: {
    flex: 1.16,
    minWidth: 0,
  },
  summaryColumn: {
    flex: 0.84,
    minWidth: 0,
    gap: 14,
  },
  sectionHeadingRow: {
    minHeight: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    marginBottom: 12,
  },
  sectionHeading: {
    color: COLORS.text.primary,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  },
  comparisonLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 2,
  },
  comparisonLinkText: {
    color: COLORS.primary,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '700',
  },
  planList: {
    gap: 10,
  },
  planOption: {
    minHeight: 88,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 18,
    paddingVertical: 15,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DDE3EC',
    backgroundColor: '#F8FAFC',
  },
  planOptionSelected: {
    borderWidth: 2,
    borderColor: COLORS.primary,
    backgroundColor: '#F3F6FF',
    paddingHorizontal: 17,
    paddingVertical: 14,
  },
  planOptionCopy: {
    flex: 1,
    minWidth: 0,
  },
  planOptionTitle: {
    color: COLORS.text.primary,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '700',
  },
  planOptionDescription: {
    marginTop: 4,
    color: COLORS.text.secondary,
    fontSize: 13,
    lineHeight: 18,
  },
  radio: {
    width: 20,
    height: 20,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#9CA3AF',
    backgroundColor: '#FFFFFF',
  },
  radioSelected: {
    borderWidth: 2,
    borderColor: COLORS.primary,
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: COLORS.primary,
  },
  billingHeading: {
    marginTop: 22,
    marginBottom: 10,
    color: COLORS.text.primary,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  },
  billingCycleRow: {
    flexDirection: 'row',
    gap: 10,
  },
  billingCycleOption: {
    minHeight: 46,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DDE3EC',
    backgroundColor: '#F8FAFC',
  },
  billingCycleOptionSelected: {
    borderWidth: 2,
    borderColor: COLORS.primary,
    backgroundColor: '#F3F6FF',
  },
  billingCycleText: {
    color: COLORS.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  billingCycleTextSelected: {
    color: COLORS.primary,
  },
  warningCard: {
    padding: 18,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#F3CF78',
    backgroundColor: '#FFFBEB',
  },
  warningHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  warningIcon: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    backgroundColor: '#FEF3C7',
  },
  warningTitle: {
    flex: 1,
    color: '#92400E',
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '800',
  },
  warningMessage: {
    marginTop: 12,
    color: '#A14B0A',
    fontSize: 13,
    lineHeight: 19,
  },
  loadingPricingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  loadingPricingText: {
    color: COLORS.text.secondary,
    fontSize: 12,
    lineHeight: 17,
  },
  priceSummary: {
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(180, 83, 9, 0.16)',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  priceDivider: {
    height: 1,
    marginVertical: 10,
    backgroundColor: 'rgba(180, 83, 9, 0.13)',
  },
  priceLabel: {
    flex: 1,
    color: COLORS.text.secondary,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
  },
  priceValue: {
    color: COLORS.text.primary,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '600',
  },
  priceValueStrong: {
    color: COLORS.text.primary,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '800',
  },
  billingNote: {
    marginTop: 12,
    color: '#A14B0A',
    fontSize: 12,
    lineHeight: 17,
  },
  acknowledgement: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DDE3EC',
    backgroundColor: '#FFFFFF',
  },
  acknowledgementSelected: {
    borderColor: '#B8C7F8',
    backgroundColor: '#F6F8FF',
  },
  acknowledgementText: {
    flex: 1,
    color: COLORS.text.primary,
    fontSize: 13,
    lineHeight: 19,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 18,
    borderTopWidth: 1,
    borderTopColor: '#E7EBF1',
    backgroundColor: '#FBFCFE',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  footerDesktop: {
    justifyContent: 'flex-end',
    paddingHorizontal: 28,
  },
  footerButton: {
    minWidth: 144,
  },
  footerButtonMobile: {
    minWidth: 0,
    flex: 1,
  },
});
